import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Users, Search, Loader2, LogIn, Pencil, Trash2,
  CheckCircle, XCircle, ShieldCheck, ShieldOff, ArrowUpDown,
  ChevronLeft, ChevronRight, MessageSquare, Eye, EyeOff,
  Wallet, Hash, FileText, User as UserIcon, Plus, Minus, Info, Calendar
} from 'lucide-react';

const PAGE_SIZE = 15;

const PLAN_STYLES = {
  'plan-free':       { label: 'Free',       cls: 'bg-slate-500/10 text-slate-400 border-slate-500/20' },
  'plan-starter':    { label: 'Starter',    cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  'plan-business':   { label: 'Business',   cls: 'bg-violet-500/10 text-violet-400 border-violet-500/20' },
  'plan-enterprise': { label: 'Enterprise', cls: 'bg-amber-500/10 text-amber-500 border-amber-500/20' },
};

const PlanBadge = ({ plan, planId }) => {
  const style = PLAN_STYLES[planId] || null;
  const label = plan || 'Free';
  const cls = style ? style.cls : 'bg-muted/50 text-muted-foreground border-border';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cls}`}>
      {label}
    </span>
  );
};

const AdminMerchants = () => {
  const { user, token, updateUser } = useAuth();
  const isSupport = user?.member_role === 'support';
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchType, setSearchType] = useState('name'); // name | wallet | txn | invoice
  const [searchResults, setSearchResults] = useState(null); // null = use local filter
  const [searching, setSearching] = useState(false);
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Impersonate loading
  const [impersonating, setImpersonating] = useState(null);

  // Adjust balance modal
  const [balanceModal, setBalanceModal] = useState(null); // merchant object
  const [balanceOp, setBalanceOp] = useState('add'); // 'add' | 'deduct'
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceReason, setBalanceReason] = useState('');
  const [balanceSaving, setBalanceSaving] = useState(false);

  // Set expiry modal
  const [expiryModal, setExpiryModal] = useState(null); // merchant object
  const [expiryDate, setExpiryDate] = useState('');
  const [expirySaving, setExpirySaving] = useState(false);

  const openBalanceModal = (m) => {
    setBalanceModal(m);
    setBalanceOp('add');
    setBalanceAmount('');
    setBalanceReason('');
  };

  const openExpiryModal = (m) => {
    setExpiryModal(m);
    // Pre-fill with existing expiry if available
    const existing = m.plan_expiry ? m.plan_expiry.split('T')[0] : '';
    setExpiryDate(existing);
  };

  const handleSetExpiry = async () => {
    if (!expiryDate) { toast.error('Please select a date'); return; }
    setExpirySaving(true);
    try {
      await axios.put(
        `${API}/admin/merchants/${expiryModal.id}/set-expiry`,
        { expiry_date: expiryDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Expiry updated for ${expiryModal.full_name}`);
      setExpiryModal(null);
      fetchMerchants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update expiry');
    } finally { setExpirySaving(false); }
  };

  const handleAdjustBalance = async () => {
    const amt = parseFloat(balanceAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid positive amount'); return; }
    if (!balanceReason.trim()) { toast.error('Reason is required'); return; }
    setBalanceSaving(true);
    try {
      const finalAmount = balanceOp === 'deduct' ? -amt : amt;
      const { data } = await axios.post(
        `${API}/admin/merchants/${balanceModal.id}/adjust-balance`,
        { amount: finalAmount, reason: balanceReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Balance ${balanceOp === 'add' ? 'added' : 'deducted'}: $${amt} USDT. New balance: $${data.new_balance.toFixed(2)}`);
      setBalanceModal(null);
      // Refresh merchants list to reflect new balance
      fetchMerchants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to adjust balance');
    } finally { setBalanceSaving(false); }
  };

  const fetchMerchants = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/merchants`);
      setMerchants(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMerchants(); }, [fetchMerchants]);

  // Backend search for wallet/txn/invoice types
  const handleSearch = useCallback(async (q, type) => {
    if (!q.trim()) { setSearchResults(null); return; }
    if (type === 'name') { setSearchResults(null); return; } // client-side
    setSearching(true);
    try {
      const { data } = await axios.get(`${API}/admin/merchants/search`, { params: { q, type } });
      setSearchResults(data);
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => handleSearch(search, searchType), 400);
    return () => clearTimeout(timer);
  }, [search, searchType, handleSearch]);

  // Filter + sort
  const baseList = searchResults !== null ? searchResults : merchants;
  const filtered = baseList
    .filter(m => {
      if (searchType !== 'name' || !search.trim()) return true;
      const q = search.toLowerCase();
      return m.full_name?.toLowerCase().includes(q) ||
        m.email?.toLowerCase().includes(q) ||
        m.business_name?.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      let av = a[sortField] ?? '';
      let bv = b[sortField] ?? '';
      if (sortField === 'total_transactions') { av = a.total_transactions || 0; bv = b.total_transactions || 0; }
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  // ── Edit ──────────────────────────────────────────────────────────────
  const openEdit = (m) => {
    setEditing(m);
    setShowNewPassword(false);
    setEditForm({
      full_name: m.full_name || '',
      business_name: m.business_name || '',
      is_verified: m.is_verified ?? true,
      is_active: m.is_active ?? true,
      preferred_currency: m.preferred_currency || 'USD',
      widget_price: m.widget_price ?? '',
      new_password: '',
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    setEditSaving(true);
    try {
      const { data } = await axios.patch(`${API}/admin/merchants/${editing.id}`, editForm);
      setMerchants(ms => ms.map(m => m.id === editing.id ? { ...m, ...data } : m));
      toast.success('Merchant updated');
      setEditOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update merchant');
    } finally { setEditSaving(false); }
  };

  // ── Delete ────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/merchants/${deleteTarget.id}`);
      setMerchants(ms => ms.filter(m => m.id !== deleteTarget.id));
      toast.success(`Merchant "${deleteTarget.full_name}" deleted`);
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete merchant');
    } finally { setDeleting(false); }
  };

  // ── Impersonate (backend login) ───────────────────────────────────────
  const handleImpersonate = async (merchant) => {
    setImpersonating(merchant.id);
    try {
      const { data } = await axios.post(`${API}/admin/merchants/${merchant.id}/impersonate`);
      // Open merchant dashboard in new tab with the token
      const origin = window.location.origin;
      const url = `${origin}/login?impersonate_token=${data.access_token}`;
      window.open(url, '_blank');
      toast.success(`Logged in as ${data.merchant_email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to impersonate merchant');
    } finally { setImpersonating(null); }
  };

  const SortHeader = ({ field, label }) => (
    <button
      className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground whitespace-nowrap"
      onClick={() => toggleSort(field)}
    >
      {label}
      <ArrowUpDown className={`h-3 w-3 ${sortField === field ? 'text-primary' : ''}`} />
    </button>
  );

  return (
    <AdminLayout title="Merchants">
      <div className="space-y-5" data-testid="admin-merchants-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Merchants</h2>
            <p className="text-sm text-muted-foreground">{merchants.length} registered merchants</p>
          </div>

          {/* Search bar + type pills */}
          <div className="flex flex-col gap-2 w-full md:w-auto">
            <div className="flex gap-1.5 flex-wrap">
              {[
                { id: 'name', label: 'Name / Email', icon: UserIcon },
                { id: 'wallet', label: 'Wallet', icon: Wallet },
                { id: 'txn', label: 'Transaction ID', icon: Hash },
                { id: 'invoice', label: 'Invoice #', icon: FileText },
              ].map(({ id, label, icon: Icon }) => (
                <button key={id} onClick={() => { setSearchType(id); setSearch(''); setSearchResults(null); setPage(1); }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all ${searchType === id ? 'bg-primary text-white border-primary' : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/40'}`}
                  data-testid={`search-type-${id}`}>
                  <Icon className="h-3 w-3" />{label}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />}
              <Input
                placeholder={
                  searchType === 'wallet' ? 'Search by wallet address…' :
                  searchType === 'txn' ? 'Search by transaction ID / hash…' :
                  searchType === 'invoice' ? 'Search by invoice number…' :
                  'Search name, email, business…'
                }
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); if (searchType === 'name') setSearchResults(null); }}
                className="pl-9 pr-9 text-sm h-9"
                data-testid="merchant-search-input"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-muted-foreground" /></div>
        ) : filtered.length === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="py-16 text-center">
              <Users className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
              <p className="text-muted-foreground">{search ? 'No merchants match your search' : 'No merchants registered yet'}</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border bg-card">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3"><SortHeader field="full_name" label="Name" /></th>
                      <th className="text-left px-4 py-3"><SortHeader field="preferred_currency" label="Fiat Currency" /></th>
                      <th className="text-left px-4 py-3"><SortHeader field="plan" label="Plan" /></th>
                      <th className="text-left px-4 py-3"><SortHeader field="balance" label="Balance" /></th>
                      <th className="text-left px-4 py-3"><SortHeader field="plan_expiry" label="Expiry" /></th>
                      <th className="text-left px-4 py-3"><SortHeader field="total_transactions" label="Total TXs" /></th>
                      <th className="text-left px-4 py-3"><SortHeader field="created_at" label="Signup Date" /></th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Verification</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {paged.map(m => (
                      <tr key={m.id} className="hover:bg-muted/20 transition-colors" data-testid={`merchant-row-${m.id}`}>
                        {/* Name */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <span className="text-xs font-semibold text-primary">
                                {(m.full_name || 'M').charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-foreground leading-tight">{m.full_name}</p>
                              <p className="text-xs text-muted-foreground">{m.email}</p>
                              {m.business_name && (
                                <p className="text-xs text-muted-foreground/70">{m.business_name}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        {/* Fiat Currency */}
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs font-semibold bg-muted/50 px-2 py-0.5 rounded-md border border-border">
                            {m.preferred_currency || 'USD'}
                          </span>
                        </td>
                        {/* Plan */}
                        <td className="px-4 py-3">
                          <PlanBadge plan={m.plan_name || m.plan} planId={m.plan_id} />
                        </td>
                        {/* Balance — clickable for admin only */}
                        <td className="px-4 py-3">
                          {!isSupport ? (
                            <button
                              data-testid={`balance-btn-${m.id}`}
                              onClick={() => openBalanceModal(m)}
                              className={`font-semibold tabular-nums text-sm px-2.5 py-1 rounded-lg border transition-colors hover:opacity-80 cursor-pointer ${
                                (m.balance ?? 0) <= 0
                                  ? 'text-red-400 bg-red-500/10 border-red-500/20'
                                  : (m.balance ?? 0) < 10
                                    ? 'text-amber-400 bg-amber-500/10 border-amber-500/20'
                                    : 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
                              }`}
                            >
                              ${parseFloat(m.balance ?? 0).toFixed(2)}
                            </button>
                          ) : (
                            <span className={`font-semibold tabular-nums text-sm px-2.5 py-1 rounded-lg border ${
                              (m.balance ?? 0) <= 0
                                ? 'text-red-400 bg-red-500/10 border-red-500/20'
                                : (m.balance ?? 0) < 10
                                  ? 'text-amber-400 bg-amber-500/10 border-amber-500/20'
                                  : 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
                            }`}>
                              ${parseFloat(m.balance ?? 0).toFixed(2)}
                            </span>
                          )}
                        </td>
                        {/* Expiry — clickable for admin only */}
                        <td className="px-4 py-3 text-xs whitespace-nowrap">
                          {!isSupport ? (
                            <button
                              data-testid={`expiry-btn-${m.id}`}
                              onClick={() => openExpiryModal(m)}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg border transition-colors hover:opacity-80 cursor-pointer ${
                                m.plan_expiry
                                  ? m.in_trial
                                    ? 'text-blue-400 bg-blue-500/10 border-blue-500/20'
                                    : 'text-muted-foreground bg-muted/40 border-border hover:border-foreground/30'
                                  : 'text-muted-foreground/50 bg-muted/20 border-dashed border-border'
                              }`}
                            >
                              <Calendar className="h-3 w-3 shrink-0" />
                              {m.plan_expiry ? (
                                <>
                                  {m.in_trial && <span className="font-medium">Trial:</span>}
                                  {new Date(m.plan_expiry).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </>
                              ) : '—'}
                            </button>
                          ) : (
                            <span className={m.in_trial ? 'text-blue-400' : 'text-muted-foreground'}>
                              {m.plan_expiry ? (
                                <>
                                  {m.in_trial && <span className="font-medium mr-1">Trial:</span>}
                                  {new Date(m.plan_expiry).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </>
                              ) : <span className="text-muted-foreground/50">—</span>}
                            </span>
                          )}
                        </td>
                        {/* Total TXs */}
                        <td className="px-4 py-3">
                          <span className="text-foreground font-medium">{m.total_transactions ?? 0}</span>
                        </td>
                        {/* Signup Date */}
                        <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">
                          {m.created_at ? new Date(m.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        </td>
                        {/* Status */}
                        <td className="px-4 py-3">
                          {m.is_active !== false ? (
                            <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-[11px]" data-testid={`status-active-${m.id}`}>Active</Badge>
                          ) : (
                            <Badge className="bg-red-500/10 text-red-500 border-red-500/20 text-[11px]" data-testid={`status-inactive-${m.id}`}>Inactive</Badge>
                          )}
                        </td>
                        {/* Verification */}
                        <td className="px-4 py-3">
                          {m.is_verified ? (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-600" data-testid={`verified-${m.id}`}>
                              <ShieldCheck className="h-3.5 w-3.5" /> Verified
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-500" data-testid={`unverified-${m.id}`}>
                              <ShieldOff className="h-3.5 w-3.5" /> Unverified
                            </span>
                          )}
                        </td>
                        {/* Actions */}
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button size="sm" variant="outline"
                              className="h-7 px-2.5 text-xs border-border gap-1.5"
                              onClick={() => handleImpersonate(m)}
                              disabled={impersonating === m.id}
                              data-testid={`impersonate-btn-${m.id}`}
                            >
                              {impersonating === m.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <LogIn className="h-3 w-3" />}
                              Login
                            </Button>
                            <Button size="sm" variant="outline"
                              className="h-7 px-2 border-border"
                              onClick={() => openEdit(m)}
                              data-testid={`edit-btn-${m.id}`}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            {!isSupport && (
                              <Button size="sm" variant="outline"
                                className="h-7 px-2 border-red-500/20 text-red-500 hover:bg-red-500/10"
                                onClick={() => setDeleteTarget(m)}
                                data-testid={`delete-btn-${m.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                  </p>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <span className="text-xs text-muted-foreground px-2">{page} / {totalPages}</span>
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Edit Modal ── */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md" data-testid="edit-merchant-modal">
          <DialogHeader>
            <DialogTitle>Edit Merchant</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-1">
              <div className="space-y-1.5">
                <Label className="text-xs">Full Name</Label>
                <Input value={editForm.full_name} onChange={e => setEditForm(f => ({ ...f, full_name: e.target.value }))}
                  data-testid="edit-full-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Business Name</Label>
                <Input value={editForm.business_name} onChange={e => setEditForm(f => ({ ...f, business_name: e.target.value }))}
                  data-testid="edit-business-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Preferred Currency</Label>
                <Input value={editForm.preferred_currency} onChange={e => setEditForm(f => ({ ...f, preferred_currency: e.target.value.toUpperCase() }))}
                  placeholder="USD" maxLength={5} data-testid="edit-currency-input" />
              </div>

              {/* New Password */}
              <div className="space-y-1.5 pt-2 border-t border-border">
                <Label className="text-xs flex items-center gap-1.5 font-semibold">
                  <Eye className="h-3.5 w-3.5 text-primary" /> Change Password
                </Label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={editForm.new_password || ''}
                    onChange={e => setEditForm(f => ({ ...f, new_password: e.target.value }))}
                    placeholder="Leave blank to keep current password"
                    className="pr-10"
                    data-testid="edit-new-password-input"
                  />
                  <button type="button" onClick={() => setShowNewPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">Min 6 characters. Leave blank to keep current.</p>
              </div>

              {/* Widget Price */}
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  <MessageSquare className="h-3.5 w-3.5 text-primary" /> Payment Widget Price ($/month)
                </Label>
                <Input type="number" min="0" step="0.01"
                  value={editForm.widget_price}
                  onChange={e => setEditForm(f => ({ ...f, widget_price: e.target.value === '' ? '' : parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00" data-testid="edit-widget-price-input" />
                <p className="text-xs text-muted-foreground">Price auto-assigned from Payment Widget configuration.</p>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
                <div>
                  <p className="text-sm font-medium text-foreground">Account Active</p>
                  <p className="text-xs text-muted-foreground">Allow merchant to log in</p>
                </div>
                <Switch checked={editForm.is_active} onCheckedChange={v => setEditForm(f => ({ ...f, is_active: v }))}
                  data-testid="edit-active-toggle" />
              </div>

              {/* Verification */}
              <div className={`flex items-center justify-between p-3 rounded-lg border ${editForm.is_verified ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-amber-500/5 border-amber-500/20'}`}>
                <div>
                  <p className="text-sm font-medium text-foreground flex items-center gap-2">
                    {editForm.is_verified
                      ? <><ShieldCheck className="h-4 w-4 text-emerald-500" /> Email Verified</>
                      : <><ShieldOff className="h-4 w-4 text-amber-500" /> Email Unverified</>}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {editForm.is_verified ? 'Account can log in normally' : 'Toggle to manually verify this account'}
                  </p>
                </div>
                <Switch checked={!!editForm.is_verified} onCheckedChange={v => setEditForm(f => ({ ...f, is_verified: v }))}
                  data-testid="edit-verified-toggle" />
              </div>

              <p className="text-xs text-muted-foreground">Email: <span className="font-mono text-foreground">{editing.email}</span></p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={editSaving} className="bg-primary text-white" data-testid="save-edit-btn">
              {editSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle className="h-4 w-4 mr-1.5" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirm ── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-merchant-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Merchant</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.full_name}</strong> ({deleteTarget?.email})?
              This will permanently remove their account, wallets, and invoices.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Adjust Balance Modal ── */}
      <Dialog open={!!balanceModal} onOpenChange={open => !open && setBalanceModal(null)}>
        <DialogContent className="max-w-sm" data-testid="adjust-balance-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-emerald-400" /> Adjust Balance
            </DialogTitle>
          </DialogHeader>
          {balanceModal && (
            <div className="space-y-4 pt-1">
              {/* Merchant info + current balance */}
              <div className="rounded-xl border border-border bg-muted/40 p-3 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Merchant</span>
                  <span className="font-semibold">{balanceModal.full_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Current Balance</span>
                  <span className={`font-bold ${(balanceModal.balance ?? 0) <= 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    ${parseFloat(balanceModal.balance ?? 0).toFixed(2)} USDT
                  </span>
                </div>
              </div>

              {/* Add / Deduct toggle */}
              <div className="flex rounded-lg overflow-hidden border border-border">
                <button
                  data-testid="balance-op-add"
                  onClick={() => setBalanceOp('add')}
                  className={`flex-1 py-2 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
                    balanceOp === 'add' ? 'bg-emerald-500 text-white' : 'bg-transparent text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Plus className="h-3.5 w-3.5" /> Add
                </button>
                <button
                  data-testid="balance-op-deduct"
                  onClick={() => setBalanceOp('deduct')}
                  className={`flex-1 py-2 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
                    balanceOp === 'deduct' ? 'bg-red-500 text-white' : 'bg-transparent text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Minus className="h-3.5 w-3.5" /> Deduct
                </button>
              </div>

              {/* Amount */}
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Amount (USDT)</Label>
                <Input
                  data-testid="balance-amount-input"
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  value={balanceAmount}
                  onChange={e => setBalanceAmount(e.target.value)}
                />
                {balanceAmount && parseFloat(balanceAmount) > 0 && (
                  <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    New balance: <span className="font-semibold text-foreground ml-1">
                      ${(parseFloat(balanceModal.balance ?? 0) + (balanceOp === 'add' ? 1 : -1) * parseFloat(balanceAmount || 0)).toFixed(2)} USDT
                    </span>
                  </p>
                )}
              </div>

              {/* Reason */}
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Reason <span className="text-red-400">*</span></Label>
                <Input
                  data-testid="balance-reason-input"
                  placeholder="e.g. Promotional credit, manual refund…"
                  value={balanceReason}
                  onChange={e => setBalanceReason(e.target.value)}
                />
              </div>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => setBalanceModal(null)}>Cancel</Button>
                <Button
                  data-testid="confirm-adjust-balance-btn"
                  onClick={handleAdjustBalance}
                  disabled={balanceSaving || !balanceAmount || !balanceReason.trim()}
                  className={`flex-1 text-white ${balanceOp === 'add' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'}`}
                >
                  {balanceSaving
                    ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    : balanceOp === 'add' ? <Plus className="h-4 w-4 mr-1.5" /> : <Minus className="h-4 w-4 mr-1.5" />
                  }
                  {balanceOp === 'add' ? 'Add Balance' : 'Deduct Balance'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Set Expiry Modal ── */}
      <Dialog open={!!expiryModal} onOpenChange={open => !open && setExpiryModal(null)}>
        <DialogContent className="max-w-sm" data-testid="set-expiry-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" /> Set Plan Expiry
            </DialogTitle>
          </DialogHeader>
          {expiryModal && (
            <div className="space-y-4 pt-1">
              {/* Merchant info */}
              <div className="rounded-xl border border-border bg-muted/40 p-3 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Merchant</span>
                  <span className="font-semibold">{expiryModal.full_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Current Expiry</span>
                  <span className="font-semibold text-foreground">
                    {expiryModal.plan_expiry
                      ? new Date(expiryModal.plan_expiry).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                      : '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Plan</span>
                  <span className="font-semibold">{expiryModal.plan_name || expiryModal.plan || 'Free'}</span>
                </div>
              </div>

              {/* Date picker */}
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">
                  New Expiry Date <span className="text-red-400">*</span>
                </Label>
                <input
                  data-testid="expiry-date-input"
                  type="date"
                  value={expiryDate}
                  onChange={e => setExpiryDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full h-10 px-3 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
                />
                {expiryDate && (
                  <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    New expiry: <span className="font-semibold text-foreground ml-1">
                      {new Date(expiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </span>
                  </p>
                )}
              </div>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => setExpiryModal(null)}>Cancel</Button>
                <Button
                  data-testid="confirm-set-expiry-btn"
                  onClick={handleSetExpiry}
                  disabled={expirySaving || !expiryDate}
                  className="flex-1 bg-primary text-white"
                >
                  {expirySaving
                    ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    : <Calendar className="h-4 w-4 mr-1.5" />
                  }
                  Set Expiry
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </AdminLayout>
  );
};

export default AdminMerchants;
